import React, {useCallback, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    buildContextSuggestDismissGoal,
    buildContextSuggestLinkGoal,
    contextSuggestKey,
    normalizeContextSuggestions,
} from '../parseContextSuggestions';

const COMPACT_ACTION_SX = {
    minWidth: 0,
    px: 1,
    py: 0.25,
    fontSize: 11,
    textTransform: 'none',
};

function typeBadgeLabel(type) {
    return type === 'chart' ? 'Chart' : 'FINTEL';
}

function SuggestionRow({
    suggestion,
    status,
    onLink,
    onDismiss,
}) {
    const title = suggestion.title?.trim() || 'Untitled';
    const isBusy = status === 'linking' || status === 'dismissing';

    return (
        <Box
            data-testid={`context-suggest-row-${contextSuggestKey(suggestion)}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography
                    variant="body2"
                    sx={{fontSize: 12, fontWeight: 500, wordBreak: 'break-word', flex: 1}}
                >
                    {title}
                </Typography>
                <Chip
                    size="small"
                    label={typeBadgeLabel(suggestion.type)}
                    data-testid={`context-suggest-type-${suggestion.type}`}
                    sx={{height: 20, fontSize: 10, fontWeight: 600}}
                />
            </Stack>

            {status === 'linked' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main'}}>
                    <CheckCircleOutlineIcon sx={{fontSize: 16}}/>
                    <Typography variant="caption" sx={{fontWeight: 600}}>Linked</Typography>
                </Stack>
            ) : status === 'dismissed' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'warning.main'}}>
                    <RemoveCircleOutlineIcon data-testid="context-suggest-dismissed-icon" sx={{fontSize: 16}}/>
                    <Typography variant="caption" sx={{fontWeight: 600, color: 'warning.main'}}>
                        Dismissed
                    </Typography>
                </Stack>
            ) : (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{alignSelf: 'flex-end'}}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={onDismiss}
                        disabled={isBusy}
                        aria-label={`Dismiss ${title}`}
                        sx={COMPACT_ACTION_SX}
                    >
                        Dismiss
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={onLink}
                        disabled={isBusy}
                        aria-label={`Link ${title}`}
                        startIcon={isBusy && status === 'linking'
                            ? <CircularProgress size={10} color="inherit"/>
                            : null}
                        sx={COMPACT_ACTION_SX}
                    >
                        Link
                    </Button>
                </Stack>
            )}
        </Box>
    );
}

/**
 * Per-suggestion confirm UI for Pure LLM context-expansion offers.
 *
 * @param {{
 *   suggestions?: Array<{type: string, id: string, title?: string|null}>,
 *   onLink: (suggestion: object) => void|Promise<void>,
 *   onDismiss: (suggestion: object) => void|Promise<void>,
 *   onDismissAll?: () => void,
 * }} props
 */
const ContextSuggestPreview = ({
    suggestions: suggestionsProp,
    onLink,
    onDismiss,
    onDismissAll,
}) => {
    const suggestions = useMemo(
        () => normalizeContextSuggestions(suggestionsProp),
        [suggestionsProp]
    );
    const [statuses, setStatuses] = useState({});

    const pendingEntries = useMemo(
        () => suggestions
            .map((suggestion) => ({suggestion, key: contextSuggestKey(suggestion)}))
            .filter(({key}) => {
                const status = statuses[key];
                return status !== 'linked' && status !== 'dismissed';
            }),
        [statuses, suggestions]
    );

    const handleLink = useCallback(async (suggestion) => {
        const key = contextSuggestKey(suggestion);
        setStatuses((current) => ({...current, [key]: 'linking'}));
        try {
            await onLink?.(suggestion);
            setStatuses((current) => ({...current, [key]: 'linked'}));
        } catch (error) {
            console.error('[ContextSuggestPreview] Link failed', error);
            setStatuses((current) => {
                const next = {...current};
                delete next[key];
                return next;
            });
        }
    }, [onLink]);

    const handleDismiss = useCallback(async (suggestion) => {
        const key = contextSuggestKey(suggestion);
        setStatuses((current) => ({...current, [key]: 'dismissing'}));
        try {
            await onDismiss?.(suggestion);
            setStatuses((current) => ({...current, [key]: 'dismissed'}));
        } catch (error) {
            console.error('[ContextSuggestPreview] Dismiss failed', error);
            setStatuses((current) => ({...current, [key]: 'dismissed'}));
        }
    }, [onDismiss]);

    const handleDismissAll = useCallback(() => {
        const next = {};
        for (const {key} of pendingEntries) {
            next[key] = 'dismissed';
        }
        setStatuses((current) => ({...current, ...next}));
        onDismissAll?.();
    }, [onDismissAll, pendingEntries]);

    if (!suggestions.length) {
        return null;
    }

    const showBulkDismiss = pendingEntries.length >= 2;

    return (
        <Box
            data-testid="context-suggest-preview"
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.default',
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <LibraryAddOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: 13}}>
                        Suggested context
                    </Typography>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{fontSize: 11}}>
                    Link a FINTEL or chart into this conversation, or dismiss the offer.
                </Typography>

                <Stack spacing={0.75}>
                    {suggestions.map((suggestion) => {
                        const key = contextSuggestKey(suggestion);
                        return (
                            <SuggestionRow
                                key={key}
                                suggestion={suggestion}
                                status={statuses[key] || null}
                                onLink={() => handleLink(suggestion)}
                                onDismiss={() => handleDismiss(suggestion)}
                            />
                        );
                    })}
                </Stack>

                {showBulkDismiss ? (
                    <Stack direction="row" justifyContent="center" sx={{pt: 1}}>
                        <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            onClick={handleDismissAll}
                            aria-label="Dismiss all context suggestions"
                            sx={{minWidth: 0, px: 1.25}}
                        >
                            Dismiss All
                        </Button>
                    </Stack>
                ) : null}
            </Stack>
        </Box>
    );
};

export {
    buildContextSuggestDismissGoal,
    buildContextSuggestLinkGoal,
    contextSuggestKey,
};
export default ContextSuggestPreview;
